import { ACSC, SYSTEM_PROMPT, T, URGENCY } from "./preScreeningConfig";

// ---------- Parsers ----------
export function comp(field, i) {
    if (!field) return null;
    const parts = field.split("^");
    return parts[i] || null;
}
export function hl7Ts(s) {
    if (!s) return null;
    const m = s.match(/^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?/);
    if (!m) return null;
    return `${m[1]}-${m[2]}-${m[3]}${m[4] ? ` ${m[4]}:${m[5] || "00"}` : ""}`;
}

export function parseHL7(text, fileName) {
    const segs = {};
    text.trim().split(/[\r\n]+/).forEach((line) => {
        if (line.length < 4 || !line.includes("|")) return;
        const id = line.slice(0, 3);
        (segs[id] = segs[id] || []).push(line.split("|"));
    });
    const msh = (segs.MSH || [[]])[0];
    const pid = (segs.PID || [[]])[0];
    const pv1 = (segs.PV1 || [[]])[0];
    const evn = (segs.EVN || [[]])[0];

    const diagnoses = (segs.DG1 || []).map((d) => ({
        code: comp(d[3], 0),
        text: comp(d[3], 1),
    })).filter((d) => d.code);

    return {
        fileName,
        format: "HL7 v2",
        messageType: msh[8] || "ADT^?",
        eventTs: hl7Ts(evn?.[2]) || hl7Ts(msh[6]),
        mrn: comp(pid[3], 0),
        patientName: `${comp(pid[5], 1) || ""} ${comp(pid[5], 0) || ""}`.trim(),
        dob: hl7Ts(pid[7]),
        patientClass: pv1[2] || null,
        facility: comp(pv1[3], 3),
        dischargeDisposition: pv1[36] || null,
        diagnoses,
        narratives: {},
    };
}

export function parseCCDA(xml, fileName) {
    const dom = new DOMParser().parseFromString(xml, "text/xml");
    const q = (sel) => dom.getElementsByTagName(sel);
    const get = (tag, attr) => {
        const el = q(tag)[0];
        return el ? (attr ? el.getAttribute(attr) : el.textContent) : null;
    };
    const wanted = {
        "11535-2": "Discharge diagnosis",
        "10164-2": "History of present illness",
        "8648-8": "Hospital course",
        "18776-5": "Discharge plan",
        "10160-0": "Medications",
        "29762-2": "Social history",
    };
    const narratives = {};
    Array.from(q("section")).forEach((sec) => {
        const code = sec.getElementsByTagName("code")[0]?.getAttribute("code");
        const label = wanted[code];
        if (!label) return;
        const txt = sec.getElementsByTagName("text")[0]?.textContent?.replace(/\s+/g, " ").trim();
        if (txt) narratives[label] = txt.slice(0, 3000);
    });
    const given = get("given"), family = get("family");
    return {
        fileName,
        format: "C-CDA",
        messageType: "CCDA",
        eventTs: hl7Ts(q("effectiveTime")[0]?.getAttribute("value")),
        mrn: q("id")[0]?.getAttribute("extension"),
        patientName: `${given || ""} ${family || ""}`.trim(),
        dob: hl7Ts(get("birthTime", "value")),
        patientClass: null,
        facility: Array.from(q("name")).map((n) => n.textContent).find((t) => t && t === t.toUpperCase() && t.length > 5) || null,
        dischargeDisposition: null,
        diagnoses: [],
        narratives,
    };
}

// ---------- Deterministic rules ----------
export async function aiTriage(enc, flags, rosterRow) {
    const payload = {
        structured: {
            message_type: enc.messageType,
            event_time: enc.eventTs,
            facility: enc.facility,
            patient_class: enc.patientClass,
            discharge_disposition: enc.dischargeDisposition,
            diagnoses: enc.diagnoses,
        },
        rule_flags: flags,
        narratives: enc.narratives,
        ecm_context: rosterRow
            ? { tier: rosterRow.tier, care_manager: rosterRow.careManager, known_conditions: rosterRow.conditions }
            : { note: "Patient not on simulated roster; treated as ECM member for demo" },
    };
    const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 1000,
            system: SYSTEM_PROMPT,
            messages: [{ role: "user", content: `Triage this encounter:\n\n${JSON.stringify(payload, null, 2)}` }],
        }),
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();
    const text = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
}

export function fallbackTriage(flags) {
    const urgent = flags.some((f) => f.includes("Inpatient discharge") || f.includes("ED discharge"));
    return {
        urgency: urgent ? "within_24h" : flags.length ? "within_72h" : "routine",
        primary_reason: "AI triage unavailable — deterministic rules only. Manual review required.",
        clinical: flags.filter((f) => f.startsWith("Priority dx") || f.includes("discharge")),
        behavioral: [],
        social: [],
        recommended_outreach: "Review source document manually and contact patient per ECM protocol.",
        talking_points: [],
        confidence: 0.3,
        fallback: true,
    };
}

// ---------- Browser-side PDF export (synthetic data only) ----------
export function exportCasePDF(item) {
    const { enc, rosterRow, flags, triage, counts, summary, slaBreached, slaCategory, slaDeadline, waitingMinutes, patientRiskLevel, outreachStatus } = item;
    const u = URGENCY[triage.urgency] || URGENCY.routine;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const domainBlock = (label, color, items) =>
        (items && items.length)
            ? `<div class="domain"><div class="dlabel" style="color:${color}">${label}</div>${items.map((i) => `<div class="ditem">${esc(i)}</div>`).join("")}</div>`
            : "";
    const list = (items) => (items && items.length)
        ? `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>` : "<p class='muted'>None recorded.</p>";

    const fmtDeadline = slaDeadline
        ? new Date(slaDeadline).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
        : null;

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>ECM Case — ${esc(enc.mrn)}</title>
  <style>
    @page { size: letter; margin: 0.7in; }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, system-ui, sans-serif; color: #15201D; font-size: 12px; line-height: 1.5; }
    .banner { display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 3px solid #0E5448; padding-bottom: 10px; margin-bottom: 14px; }
    h1 { font-size: 17px; margin: 0; color:#0A3D34; }
    .sub { font-size: 11px; color:#6B7470; margin-top:3px; }
    .pill { font-size: 11px; font-weight: 700; color:#fff; background:${u.color}; padding:4px 12px; border-radius: 999px; white-space:nowrap; }
    .risk-pill { font-size: 11px; font-weight: 700; padding:3px 10px; border-radius: 999px; white-space:nowrap; margin-top: 5px; display: inline-block; }
    .sla-breach { font-size: 11px; font-weight: 700; color: #C0392B; background: #FDEDEC; padding:3px 10px; border-radius: 5px; margin-top: 5px; display: inline-block; }
    .sla-ok { font-size: 11px; color: #6B7470; background: #F4F4F4; padding:3px 10px; border-radius: 5px; margin-top: 5px; display: inline-block; }
    .grid { display:grid; grid-template-columns: 1fr 1fr; gap:4px 18px; margin: 4px 0 14px; }
    .grid div { font-size: 11.5px; } .grid b { color:#6B7470; font-weight:600; }
    .reason { background:#FAF8F3; border-left:4px solid ${u.color}; padding:9px 12px; margin-bottom:14px; font-size:12.5px; }
    .summary-box { background:#F7F9F8; border:1px solid #D6EAE4; border-radius:7px; padding:10px 13px; margin-bottom:14px; font-size:12px; line-height:1.6; }
    .summary-box h3 { margin:0 0 5px; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#0E5448; }
    .counters { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:14px; }
    .counter-chip { font-size:11px; font-weight:700; padding:3px 9px; border-radius:5px; }
    .domain { margin-bottom:10px; }
    .dlabel { font-size:11px; font-weight:700; letter-spacing:1px; text-transform:uppercase; margin-bottom:3px; }
    .ditem { padding:2px 0 2px 12px; }
    .action { background:#0E54480D; border:1px solid #0E544833; border-radius:8px; padding:11px 13px; margin:6px 0 14px; }
    .action h3 { margin:0 0 5px; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#0E5448; }
    .sec { font-size:11px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:#6B7470; margin:14px 0 5px; border-top:1px solid #E3DFD5; padding-top:10px; }
    ul { margin:4px 0; padding-left:18px; } li { margin:2px 0; }
    .muted { color:#6B7470; }
    .mono { font-family: ui-monospace, Menlo, monospace; font-size:11px; }
    .foot { margin-top:18px; padding-top:10px; border-top:1px solid #E3DFD5; font-size:10px; color:#6B7470; }
  </style></head><body>

    <div class="banner">
      <div>
        <h1>${esc(enc.patientName) || "Unknown patient"}</h1>
        <div class="sub mono">MRN ${esc(enc.mrn)} &nbsp;·&nbsp; DOB ${esc(enc.patientDob) || "—"} &nbsp;·&nbsp; ${esc(enc.patientGender) || "—"}</div>
        <div class="sub">${esc(enc.facility) || "—"}</div>
      </div>
      <div style="text-align:right">
        <div class="pill">${esc(u.label)}</div>
        <div class="sub" style="margin-top:6px">AI confidence ${Math.round((triage.confidence || 0) * 100)}%</div>
        ${patientRiskLevel ? `<div class="risk-pill" style="background:${({ Critical: "#FDEDEC", High: "#FEF3E2", Medium: "#FEF9E7", Low: "#EAFAF1" }[patientRiskLevel] || "#F4F4F4")}; color:${({ Critical: "#C0392B", High: "#E07B00", Medium: "#B7950B", Low: "#1E8449" }[patientRiskLevel] || "#6B7470")}">${esc(patientRiskLevel)} Risk</div>` : ""}
        ${slaBreached
            ? `<div class="sla-breach">⚠ SLA · ${waitingMinutes} min waiting</div>`
            : `<div class="sla-ok">${esc(slaCategory)} · ${waitingMinutes} min waiting</div>`}
      </div>
    </div>

    <div class="grid">
      <div><b>Event</b> ${esc(enc.messageType)}</div>
      <div><b>Event time</b> ${esc(enc.eventTs) || "—"}</div>
      <div><b>Document type</b> ${esc(enc.format) || "—"}</div>
      <div><b>SLA deadline</b> <span style="${slaBreached ? "color:#C0392B;font-weight:700" : ""}">${fmtDeadline || "—"}</span></div>
      <div><b>ECM tier</b> ${esc(rosterRow?.tier) || "—"}</div>
      <div><b>Care manager</b> ${esc(rosterRow?.careManager) || "—"}</div>
      <div><b>Outreach status</b> ${esc(outreachStatus) || "—"}</div>
      <div><b>Review status</b> ${esc(item.status) || "—"}</div>
    </div>

    ${counts ? `
    <div class="counters">
      ${counts.criticalEvents > 0 ? `<span class="counter-chip" style="background:#FDEDEC;color:#C0392B">${counts.criticalEvents} Critical Event${counts.criticalEvents !== 1 ? "s" : ""}</span>` : ""}
      ${counts.clinical > 0 ? `<span class="counter-chip" style="background:#D5EAE3;color:#0E5448">${counts.clinical} Clinical Risk${counts.clinical !== 1 ? "s" : ""}</span>` : ""}
      ${counts.behavioral > 0 ? `<span class="counter-chip" style="background:#EDE7F6;color:#6B4E9C">${counts.behavioral} Behavioral</span>` : ""}
      ${counts.sdoh > 0 ? `<span class="counter-chip" style="background:#FEF0E4;color:#A05A21">${counts.sdoh} SDOH Risk${counts.sdoh !== 1 ? "s" : ""}</span>` : ""}
      ${counts.careGaps > 0 ? `<span class="counter-chip" style="background:#F4F4F4;color:#6B7470">${counts.careGaps} Care Gap${counts.careGaps !== 1 ? "s" : ""}</span>` : ""}
      ${counts.ruleTriggers > 0 ? `<span class="counter-chip" style="background:#F4F4F4;color:#6B7470">${counts.ruleTriggers} Rule Trigger${counts.ruleTriggers !== 1 ? "s" : ""}</span>` : ""}
    </div>` : ""}

    <div class="reason"><b>Primary reason:</b> ${esc(triage.primary_reason)}</div>

    ${summary ? `
    <div class="summary-box">
      <h3>AI Summary</h3>
      ${esc(summary)}
    </div>` : ""}

    ${domainBlock("Clinical", "#0E5448", triage.clinical)}
    ${domainBlock("Behavioral", "#6B4E9C", triage.behavioral)}
    ${domainBlock("Social / SDOH", "#A05A21", triage.social)}

    <div class="action">
      <h3>Recommended first action</h3>
      <div>${esc(triage.recommended_outreach)}</div>
    </div>

    <div class="sec">Priority diagnoses</div>
    ${list(triage.talking_points)}

    <div class="sec">Rule triggers (${flags.length})</div>
    ${flags.length ? `<ul>${flags.map((f) => `<li class="mono">${esc(f)}</li>`).join("")}</ul>` : "<p class='muted'>None.</p>"}

    <div class="foot">
      Generated ${new Date().toLocaleString()} &nbsp;·&nbsp; ${esc(enc.format)} · MRN ${esc(enc.mrn)} &nbsp;·&nbsp; AI triage is a recommendation; clinical judgment and program protocols govern outreach.
    </div>
  </body></html>`;

    const w = window.open("", "_blank");
    if (!w) {
        alert("Popup blocked — allow popups to export the PDF.");
        return;
    }
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
}

// ---------- UI atoms ----------
export function Chip({ color, children, filled }) {
    return (
        <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 0.4, padding: "3px 9px",
            borderRadius: 999, whiteSpace: "nowrap",
            background: filled ? color : `${color}1A`,
            color: filled ? "#fff" : color,
            border: filled ? "none" : `1px solid ${color}40`,
        }}>{children}</span>
    );
}

export function DomainList({ label, color, items }) {
    if (!items || !items.length) return null;
    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: "inline-block" }} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color, textTransform: "uppercase" }}>{label}</span>
            </div>
            {items.map((it, i) => (
                <div key={i} style={{ fontSize: 13.5, color: T.ink, padding: "3px 0 3px 15px", lineHeight: 1.45 }}>{it}</div>
            ))}
        </div>
    );
}

// ---------- Supervisor view ----------
export function ruleFlags(enc) {
    const flags = [];
    if (enc.messageType.startsWith("ADT^A03")) {
        if (enc.patientClass === "I") flags.push("Inpatient discharge (A03, class I)");
        if (enc.patientClass === "E") flags.push("ED discharge (A03, class E)");
    }
    if (enc.patientClass === "E") flags.push("Emergency department encounter");
    enc.diagnoses.forEach((d) => {
        if (ACSC.some((p) => d.code?.toUpperCase().startsWith(p)))
            flags.push(`Priority dx: ${d.code} ${d.text || ""}`);
    });
    if (enc.messageType === "CCDA" && Object.keys(enc.narratives).length)
        flags.push("Discharge summary narrative received");
    return [...new Set(flags)];
}


export function fmtMins(m) {
    if (m == null) return "—";
    if (m < 1) return "<1 min";
    if (m < 60) return `${Math.round(m)} min`;
    return `${(m / 60).toFixed(1)} h`;
}

export function KpiTile({ label, value, sub, color }) {
    return (
        <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: "14px 16px", flex: "1 1 150px", minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: T.mute, textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: color || T.ink, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
            {sub && <div style={{ fontSize: 12, color: T.mute, marginTop: 3 }}>{sub}</div>}
        </div>
    );
}
